import React from "react";
import PropTypes from "prop-types";

import TileTip from "./TileTip";
import { reachedYetiGoal } from "../../helpers/beatYeti";

/**
 * "Beating the Yeti", team by team — the strip under the Beat the Yeti rate
 * tile: which teams the card was asked to break out, how many of their players
 * are ahead of the Yeti, and what share that is.
 *
 *   Technicians                       7/10  70%
 *   ▮▮▮▮▮▮▮▯▯▯
 *
 * One pip per player (green = ahead of the Yeti, grey = behind), so the
 * fraction can be read at a glance without doing the sum. A very large team
 * falls back to a plain rail, where a hundred pips would just be a smear.
 *
 * Counts come from useDerivedRevenueMetrics `teams` — players with an actual
 * score only, each against their own team's Yeti (helpers/beatYeti).
 *
 * A goal (a whole percent, or null) draws a notch on a bar at that share of
 * players beating the Yeti: green once reached, red while not. Each team carries
 * its own (`team.goal` — Technicians and Sales aim at different shares); the
 * overall bar under the big number (YetiOverall) takes the card's overall goal.
 * Hovering a notch says what the goal is and how far off it is.
 *
 * The filled part of a bar follows the notch: red until the goal is reached,
 * green from there on. With no goal there is nothing to be short of, so it
 * stays green.
 */

const MAX_PIPS = 24;
const pctOf = (rate) => `${Math.round((Number(rate) || 0) * 100)}%`;

/* The goal marker: a short upright tick straddling the bar, inside a small
   hover target (a 2px tick alone is hard to hit). The target sits on the bar's
   track wrapper, so left:N% is N% of the bar; it is centred on the goal, except
   at 100% where it is pinned to the bar's end instead of hanging past it. */
const Notch = ({ goal, rate, who }) => {
  if (!goal) return null;
  const ok = reachedYetiGoal(rate, goal);
  const now = Math.round((Number(rate) || 0) * 100);
  const end = goal >= 100;
  return (
    <TileTip
      className={`cw-yt-notchwrap${end ? " end" : ""}`}
      style={{ left: end ? "calc(100% - 14px)" : `${goal}%` }}
      eyebrow={`${who} · goal`}
      rows={[
        { label: "Goal", value: `${goal}%` },
        { label: "Beating the Yeti", value: `${now}%` },
      ]}
      verdict={{
        text: ok ? "✓ Goal reached" : `${goal - now} ${goal - now === 1 ? "point" : "points"} to go`,
        tone: ok ? "good" : "bad",
      }}
      note="Goal is the share of players who should be beating the Yeti."
    >
      <i className={`cw-yt-notch ${ok ? "ok" : "miss"}`} />
    </TileTip>
  );
};
Notch.propTypes = { goal: PropTypes.number, rate: PropTypes.number, who: PropTypes.string };
Notch.defaultProps = { goal: null, rate: 0, who: "Goal" };

/* "ok" / "miss" for a bar's fill — green only while there is no goal or it has
   been reached. */
const fillTone = (rate, goal) => (goal && !reachedYetiGoal(rate, goal) ? "miss" : "ok");

const Track = ({ beating, players, goal, who }) => {
  const rate = players ? beating / players : 0;
  const tone = fillTone(rate, goal);
  let bar;
  if (players > MAX_PIPS) {
    bar = (
      <div className={`cw-yt-rail ${tone}`} aria-hidden>
        <div className="cw-yt-rail-fill" style={{ width: `${Math.min(100, rate * 100)}%` }} />
      </div>
    );
  } else {
    bar = (
      <div className={`cw-yt-pips ${tone}`} aria-hidden>
        {Array.from({ length: players }, (_, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <i key={i} className={i < beating ? "on" : ""} />
        ))}
      </div>
    );
  }
  return (
    <div className="cw-yt-track">
      {bar}
      <Notch goal={goal} rate={rate} who={who} />
    </div>
  );
};
Track.propTypes = {
  beating: PropTypes.number.isRequired,
  players: PropTypes.number.isRequired,
  goal: PropTypes.number,
  who: PropTypes.string,
};
Track.defaultProps = { goal: null, who: "Goal" };

/* The total beside the big percentage — "4/13", the team rows added up — on the
   same line as the number, at the same right edge as the rows' own figures below
   it (the box they sit in has a 1.5px border and 13px of padding). `beating` and
   `players` are the headline's own counts, so it always matches the percentage. */
export const YetiTotal = ({ beating, players, periodLabel }) => {
  if (!players) return null;
  const rate = beating / players;
  return (
    <TileTip
      className="cw-yt-total"
      eyebrow={`All tracked teams · ${periodLabel}`}
      rows={[
        { label: "Beating the Yeti", value: String(beating) },
        { label: "Players scored", value: String(players) },
      ]}
      verdict={{ text: `${pctOf(rate)} beating`, tone: rate >= 0.5 ? "good" : "flat" }}
      note="Every tracked team added together — the same players the percentage is out of."
    >
      <span className="cw-yt-total-in">
        <b>{beating}/{players}</b>
        <span>beating</span>
      </span>
    </TileTip>
  );
};
YetiTotal.propTypes = {
  beating: PropTypes.number.isRequired,
  players: PropTypes.number.isRequired,
  periodLabel: PropTypes.string,
};
YetiTotal.defaultProps = { periodLabel: "this month" };

/* The overall bar under the big percentage: the headline rate as a fill, with
   the goal notch when one is set. `rate` is the same 0–1 the number prints. */
export const YetiOverall = ({ rate, goal }) => {
  const r = Math.max(0, Math.min(1, Number(rate) || 0));
  return (
    <div className="cw-yt-overall">
      <div className="cw-yt-track">
        <div className={`cw-yt-rail ${fillTone(r, goal)}`} aria-hidden>
          <div className="cw-yt-rail-fill" style={{ width: `${r * 100}%` }} />
        </div>
        <Notch goal={goal} rate={r} who="Overall" />
      </div>
      {goal ? <span className="cw-yt-goaltxt">Goal {goal}%</span> : null}
    </div>
  );
};
YetiOverall.propTypes = { rate: PropTypes.number, goal: PropTypes.number };
YetiOverall.defaultProps = { rate: 0, goal: null };

const YetiTeams = ({ teams, periodLabel }) => {
  if (!teams || !teams.length) return null;
  return (
    <div className="cw-yt" role="group" aria-label="Players beating the Yeti, by team">
      <div className="cw-yt-eyebrow">Beating the Yeti</div>
      {teams.map((t) => {
        const empty = !t.loading && !t.players;
        let label;
        if (t.loading) label = `${t.name}: loading`;
        else if (empty) label = `${t.name}: no scored players yet`;
        else label = `${t.name}: ${t.beating} of ${t.players} beating the Yeti, ${pctOf(t.rate)}`;
        return (
          <div className="cw-yt-row" key={t.workspaceId} role="group" aria-label={label}>
            <div className="cw-yt-head">
              <span className="cw-yt-name" title={t.name}>{t.name}</span>
              {t.loading && <span className="gmb-skel cw-yt-skel-figs" />}
              {!t.loading && empty && <span className="cw-yt-none">No scores yet</span>}
              {!t.loading && !empty && (
                <TileTip
                  eyebrow={`${t.name} · ${periodLabel}`}
                  rows={[
                    { label: "Beating the Yeti", value: String(t.beating) },
                    { label: "Players scored", value: String(t.players) },
                  ]}
                  verdict={{ text: `${pctOf(t.rate)} ahead of pace`, tone: t.rate >= 0.5 ? "good" : "flat" }}
                  note="Players with a score in this window, each against their own team's Yeti."
                >
                  <span className="cw-yt-figs">
                    <b>{t.beating}/{t.players}</b>
                    <span className="cw-yt-pct">{pctOf(t.rate)}</span>
                  </span>
                </TileTip>
              )}
            </div>
            {t.loading && <span className="gmb-skel cw-yt-skel-track" />}
            {!t.loading && !empty && <Track beating={t.beating} players={t.players} goal={t.goal} who={t.name} />}
          </div>
        );
      })}
    </div>
  );
};

YetiTeams.propTypes = {
  teams: PropTypes.arrayOf(PropTypes.shape({
    workspaceId: PropTypes.string,
    name: PropTypes.string,
    beating: PropTypes.number,
    players: PropTypes.number,
    rate: PropTypes.number,
    loading: PropTypes.bool,
    goal: PropTypes.number,
  })),
  periodLabel: PropTypes.string,
};
YetiTeams.defaultProps = { teams: [], periodLabel: "this month" };

export default YetiTeams;
